import { useCallback, useMemo, type Dispatch, type SetStateAction } from "react";

import type {
  AuthSession,
  CompanySwitchPrompt,
  CompleteInviteOnboardingInput,
  GoogleCredentialInput,
  GoogleRedirectCompleteInput,
  GoogleRedirectCompletion,
  GoogleSignupInput,
  LoginInput,
  ProfileInput,
  SignupInput,
  User,
} from "@/lib/auth/auth-api";

import * as authApi from "@/lib/auth/auth-api";
import { clearStoredMfaChallenge } from "@/lib/auth/mfa-challenge-storage";
import { clearStoredToken, setStoredToken } from "@/lib/auth/token-storage";

type AuthQueryClient = { clear: () => void };
type LoginFlowResult =
  | Extract<authApi.LoginResult, { kind: "mfa" | "onboarding" }>
  | CompanySwitchPrompt
  | null;

export interface AuthActions {
  completeGoogleRedirect: (
    input: GoogleRedirectCompleteInput,
  ) => Promise<GoogleRedirectCompletion>;
  completeInviteOnboarding: (
    input: CompleteInviteOnboardingInput,
  ) => Promise<CompanySwitchPrompt | null>;
  googleLogin: (input: GoogleCredentialInput) => Promise<LoginFlowResult>;
  googleSignup: (input: GoogleSignupInput) => Promise<void>;
  linkGoogle: (input: GoogleCredentialInput) => Promise<void>;
  login: (input: LoginInput) => Promise<LoginFlowResult>;
  logout: () => Promise<void>;
  revokeAllSessions: () => Promise<void>;
  setPasswordAuthEnabled?: () => void;
  signup: (input: SignupInput) => Promise<void>;
  switchCompany: (inviteId: string) => Promise<CompanySwitchPrompt | null>;
  updateProfile: (input: ProfileInput) => Promise<void>;
  verifyMfa: (
    input: Parameters<typeof authApi.verifyMfaLogin>[0],
  ) => Promise<CompanySwitchPrompt | null>;
}

interface AuthActionsOptions {
  queryClient: AuthQueryClient;
  setUser: Dispatch<SetStateAction<User | null>>;
}

function getCompanySwitchPrompt(
  options: AuthSession["companySwitchOptions"],
): CompanySwitchPrompt | null {
  return options.length > 0 ? { kind: "company-switch", options } : null;
}

export function useAuthActions({
  queryClient,
  setUser,
}: AuthActionsOptions): AuthActions {
  const storeSession = useCallback(
    (session: AuthSession): CompanySwitchPrompt | null => {
      setStoredToken(session.accessToken);
      const switchPrompt = getCompanySwitchPrompt(session.companySwitchOptions);
      setUser(switchPrompt ? null : session.user);
      return switchPrompt;
    },
    [setUser],
  );

  const login = useCallback(
    async (input: LoginInput): Promise<LoginFlowResult> => {
      const result = await authApi.login(input);
      return "kind" in result ? result : storeSession(result);
    },
    [storeSession],
  );

  const googleLogin = useCallback(
    async (input: GoogleCredentialInput): Promise<LoginFlowResult> => {
      const result = await authApi.googleLogin(input);
      return "kind" in result ? result : storeSession(result);
    },
    [storeSession],
  );

  const verifyMfa = useCallback(
    async (input: Parameters<typeof authApi.verifyMfaLogin>[0]) => {
      const session = await authApi.verifyMfaLogin(input);
      return storeSession(session);
    },
    [storeSession],
  );

  const signup = useCallback(
    async (input: SignupInput) => {
      storeSession(await authApi.signup(input));
    },
    [storeSession],
  );

  const googleSignup = useCallback(
    async (input: GoogleSignupInput) => {
      storeSession(await authApi.googleSignup(input));
    },
    [storeSession],
  );

  const completeInviteOnboarding = useCallback(
    async (input: CompleteInviteOnboardingInput) =>
      storeSession(await authApi.completeInviteOnboarding(input)),
    [storeSession],
  );

  const switchCompany = useCallback(
    async (inviteId: string) => storeSession(await authApi.switchCompany(inviteId)),
    [storeSession],
  );

  const completeGoogleRedirect = useCallback(
    async (input: GoogleRedirectCompleteInput): Promise<GoogleRedirectCompletion> => {
      const result = await authApi.completeGoogleRedirect(input);
      if (result.status === "organization_required" || "kind" in result.result) {
        return result;
      }
      const switchPrompt = storeSession(result.result);
      return switchPrompt ? { ...result, result: switchPrompt } : result;
    },
    [storeSession],
  );

  const linkGoogle = useCallback(
    (input: GoogleCredentialInput) => authApi.linkGoogle(input).then(() => undefined),
    [],
  );

  const updateProfile = useCallback(
    async (input: ProfileInput) => {
      setUser(await authApi.updateProfile(input));
    },
    [setUser],
  );

  const setPasswordAuthEnabled = useCallback(
    () =>
      setUser((current) =>
        current ? { ...current, passwordAuthEnabled: true } : current,
      ),
    [setUser],
  );

  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } catch {
      // Best-effort — a failed logout call must never block clearing local state.
    }
    clearStoredMfaChallenge();
    clearStoredToken();
    setUser(null);
    queryClient.clear();
  }, [queryClient, setUser]);

  const revokeAllSessions = useCallback(async () => {
    try {
      await authApi.revokeAllSessions();
    } finally {
      clearStoredMfaChallenge();
      clearStoredToken();
      setUser(null);
      queryClient.clear();
    }
  }, [queryClient, setUser]);

  return useMemo(
    () => ({
      completeGoogleRedirect,
      completeInviteOnboarding,
      googleLogin,
      googleSignup,
      linkGoogle,
      login,
      logout,
      revokeAllSessions,
      setPasswordAuthEnabled,
      signup,
      switchCompany,
      updateProfile,
      verifyMfa,
    }),
    [
      completeGoogleRedirect,
      completeInviteOnboarding,
      googleLogin,
      googleSignup,
      linkGoogle,
      login,
      logout,
      revokeAllSessions,
      setPasswordAuthEnabled,
      signup,
      switchCompany,
      updateProfile,
      verifyMfa,
    ],
  );
}
